import { inject, ModuleWithProviders, NgModule, provideAppInitializer, Provider } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { ApplicationType } from '@kros-sk/models';

import { APPLICATION_TYPE_CONFIG } from './kros-auth.config';
import { AuthStoreModule } from './store/auth-store.module';
import { configClientId, configScope } from './injection-tokens';
import { KROS_IDENTITY_SERVER_CONFIG, KrosIdentityServerConfig } from './services/auth/kros-identity-server.config';
import { KrosAuthGuardService } from './guards/kros-auth-guard.service';
import { KrosAuthInterceptor } from './interceptors/kros-auth-interceptor';
import { KrosAuthService } from './services/auth/kros-auth.service';
import { KrosLocaleInterceptor } from './interceptors/kros-locale.interceptor';

/**
 * Loading apiConfig file and obtaining User details from UserManager promise
 *
 * @param authService KrosEswAuthService
 * @param clientId string
 * @param scope string
 * @param applicationType ApplicationType
 * @returns Promise<User>
 */
export const authServiceInitializationFactory = (
  authService: KrosAuthService,
  clientId: string,
  scope: string,
  applicationType: ApplicationType
) => {
  return async (): Promise<void> => {
    return authService.initialize(clientId, scope, applicationType);
  };
};

export function provideApplicationTypeForAuth(applicationType: ApplicationType): Provider[] {
  return [
    {
      provide: APPLICATION_TYPE_CONFIG,
      useValue: applicationType,
    },
  ];
}

// @dynamic
@NgModule({ imports: [AuthStoreModule], providers: [provideHttpClient(withInterceptorsFromDi())] })
export class KrosAuthModule {
  public static forRoot(
    identityServerConfig: KrosIdentityServerConfig,
    clientId: string,
    scope: string,
    applicationType: ApplicationType
  ): ModuleWithProviders<KrosAuthModule> {
    return {
      ngModule: KrosAuthModule,
      providers: [
        KrosAuthService,
        KrosAuthGuardService,
        provideApplicationTypeForAuth(applicationType),
        {
          provide: configClientId,
          useValue: clientId,
        },
        {
          provide: configScope,
          useValue: scope,
        },
        {
          provide: KROS_IDENTITY_SERVER_CONFIG,
          useValue: identityServerConfig,
        },
        provideAppInitializer(() => {
          const initializerFn = authServiceInitializationFactory(
            inject(KrosAuthService),
            inject(configClientId),
            inject(configScope),
            inject(APPLICATION_TYPE_CONFIG)
          );
          return initializerFn();
        }),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: KrosAuthInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: KrosLocaleInterceptor,
          multi: true,
        },
      ],
    };
  }
}
